<template>
  <div class="platform-service-page">
    <section class="page-toolbar">
      <div class="toolbar-copy">
        <strong>平台客服</strong>
        <span>买家在商城里点「客服」发起的咨询会汇总到这里，可直接回复。</span>
      </div>
      <div class="toolbar-actions">
        <span class="connection-state" :class="{ online: connected }">
          {{ connected ? "实时连接中" : "点击「刷新」获取最新消息" }}
        </span>
        <button class="plain-button" type="button" :disabled="loading" @click="refresh">
          {{ loading ? "加载中..." : "刷新" }}
        </button>
      </div>
    </section>

    <p v-if="error" class="service-error">{{ error }}</p>

    <section class="service-layout">
      <aside class="service-panel service-list">
        <header class="service-panel__head">
          <strong>全部会话</strong>
          <span>{{ conversations.length }} 个 · 未读 {{ unreadTotal }}</span>
        </header>
        <div v-if="conversations.length" class="conversation-list">
          <button
            v-for="item in conversations"
            :key="item.id"
            type="button"
            class="conversation-row"
            :class="{ active: active && active.id === item.id }"
            @click="openConversation(item)"
          >
            <span class="conversation-avatar">{{ (item.buyerName || "商").slice(0, 1) }}</span>
            <span class="conversation-main">
              <strong>
                {{ item.buyerName || "商城用户" }}
                <em v-if="item.kind === 'merchant'" class="kind-tag">商家</em>
              </strong>
              <small>{{ item.lastMessage || "（暂无消息）" }}</small>
            </span>
            <span class="conversation-side">
              <em v-if="item.unread">{{ item.unread }}</em>
              <small>{{ shortTime(item.lastMessageAt) }}</small>
            </span>
          </button>
        </div>
        <div v-else-if="!loading" class="empty-state">还没有买家咨询平台客服</div>
      </aside>

      <section class="service-panel service-chat">
        <template v-if="active">
          <header class="service-panel__head">
            <strong>
              {{ active.buyerName || "商城用户" }}
              <em v-if="active.kind === 'merchant'" class="kind-tag">商家</em>
            </strong>
            <span v-if="active.productName">咨询商品：{{ active.productName }}</span>
            <span v-else>{{ active.kind === 'merchant' ? '商家咨询平台' : '平台咨询' }}</span>
          </header>
          <div ref="messageList" class="message-list">
            <div
              v-for="message in messages"
              :key="message.id"
              class="message-row"
              :class="message.sender === 'merchant' ? 'mine' : 'theirs'"
            >
              <span v-if="isImageMessage(message)" class="message-bubble message-bubble--image">
                <a
                  :href="resolveImageUrl(message.content)"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="查看原图"
                >
                  <img
                    :src="resolveImageUrl(message.content)"
                    alt="客服图片消息"
                    loading="lazy"
                    @load="scrollToBottom"
                  />
                </a>
              </span>
              <span v-else class="message-bubble">{{ message.content }}</span>
              <small>{{ shortTime(message.createdAt) }}</small>
            </div>
            <p v-if="!messages.length" class="message-empty">还没有消息，先打个招呼吧</p>
          </div>
          <div class="message-composer">
            <textarea
              v-model="draft"
              placeholder="输入回复内容，Enter 发送，Shift+Enter 换行"
              @keydown.enter.exact.prevent="send"
            ></textarea>
            <button class="primary-button" type="button" :disabled="!draft.trim() || sending" @click="send">
              {{ sending ? "发送中..." : "发送" }}
            </button>
          </div>
        </template>
        <div v-else class="empty-state">选择左侧会话开始回复</div>
      </section>
    </section>
  </div>
</template>

<script>
import {
  ensureShopToken,
  fetchPlatformConversations,
  fetchPlatformMessages,
  sendPlatformMessage,
  shopSocketUrl
} from "../api/platformService"
import { resolveBackendAssetUrl } from "../utils/apiEndpoint"

export default {
  name: "PlatformServiceView",
  data() {
    return {
      conversations: [],
      active: null,
      messages: [],
      draft: "",
      loading: true,
      sending: false,
      connected: false,
      error: "",
      socket: null,
      reconnectTimer: null
    }
  },
  computed: {
    unreadTotal() {
      return this.conversations.reduce((total, item) => total + (item.unread || 0), 0)
    }
  },
  created() {
    this.bootstrap()
  },
  beforeDestroy() {
    this.closeSocket()
  },
  methods: {
    async bootstrap() {
      this.loading = true
      this.error = ""
      try {
        await ensureShopToken()
        await this.loadConversations()
        this.connectSocket()
      } catch (error) {
        this.error = error.message
      } finally {
        this.loading = false
      }
    },
    async refresh() {
      this.loading = true
      this.error = ""
      try {
        await ensureShopToken()
        await this.loadConversations()
        this.connectSocket()
      } catch (error) {
        this.error = error.message
      } finally {
        this.loading = false
      }
    },
    async loadConversations() {
      const payload = await fetchPlatformConversations()
      this.conversations = payload.data || []
    },
    async openConversation(conversation) {
      this.active = conversation
      this.error = ""
      try {
        const payload = await fetchPlatformMessages(conversation.id)
        this.messages = payload.data || []
        conversation.unread = 0
        this.sendRead(conversation.id)
        this.scrollToBottom()
      } catch (error) {
        this.error = error.message
      }
    },
    async send() {
      const content = this.draft.trim()
      if (!content || !this.active) return
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ type: "message", conversationId: this.active.id, content }))
        this.draft = ""
        return
      }
      this.sending = true
      try {
        const payload = await sendPlatformMessage(this.active.id, content)
        this.messages.push(payload.data)
        this.draft = ""
        this.scrollToBottom()
      } catch (error) {
        this.error = error.message
      } finally {
        this.sending = false
      }
    },
    sendRead(conversationId) {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ type: "read", conversationId }))
      }
    },
    connectSocket() {
      if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) return
      const url = shopSocketUrl()
      if (!url) {
        // 尚未开放实时推送：会话靠刷新/重新进入页面拉取。
        this.connected = false
        return
      }
      try {
        this.socket = new WebSocket(url)
      } catch (error) {
        this.connected = false
        return
      }
      this.socket.onopen = () => {
        this.connected = true
        this.error = ""
      }
      this.socket.onclose = () => {
        this.connected = false
        this.socket = null
        window.clearTimeout(this.reconnectTimer)
        this.reconnectTimer = window.setTimeout(this.connectSocket, 3000)
      }
      this.socket.onerror = () => {
        this.connected = false
      }
      this.socket.onmessage = event => {
        let payload = null
        try {
          payload = JSON.parse(event.data)
        } catch (error) {
          return
        }
        this.handleEvent(payload)
      }
    },
    closeSocket() {
      window.clearTimeout(this.reconnectTimer)
      if (this.socket) {
        this.socket.close()
        this.socket = null
      }
      this.connected = false
    },
    handleEvent(payload) {
      if (payload.type === "message" && payload.conversation) {
        this.upsertConversation(payload.conversation)
        if (this.active && payload.conversation.id === this.active.id) {
          this.active = payload.conversation
          if (payload.message && !this.messages.some(item => item.id === payload.message.id)) {
            this.messages.push(payload.message)
            this.scrollToBottom()
            this.sendRead(payload.conversation.id)
          }
        }
        return
      }
      if (payload.type === "conversation" && payload.conversation) {
        this.upsertConversation(payload.conversation)
      }
    },
    upsertConversation(conversation) {
      const index = this.conversations.findIndex(item => item.id === conversation.id)
      if (index >= 0) this.$set(this.conversations, index, conversation)
      else this.conversations.unshift(conversation)
      // 没有消息的会话排到最后，避免 String(null) 把空会话顶到最前面
      this.conversations.sort((a, b) => String(b.lastMessageAt || "").localeCompare(String(a.lastMessageAt || "")))
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messageList
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    isImageMessage(message) {
      const type = message && (message.type || message.messageType)
      return String(type || "").toUpperCase() === "IMAGE"
    },
    resolveImageUrl(value) {
      return resolveBackendAssetUrl(value)
    },
    shortTime(value) {
      const text = String(value || "")
      if (!text) return ""
      const time = text.slice(11, 16)
      const date = text.slice(5, 10)
      return time || date
    }
  }
}
</script>

<style scoped>
.platform-service-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.page-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.toolbar-copy strong {
  display: block;
  color: #111827;
  font-size: 20px;
  font-weight: 700;
}

.toolbar-copy span {
  display: block;
  margin-top: 6px;
  color: #6b7280;
  font-size: 13px;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.connection-state {
  color: #9ca3af;
  font-size: 13px;
}

.connection-state.online {
  color: #059669;
}

.service-error {
  margin: 0 0 12px;
  padding: 10px 14px;
  border: 1px solid #fecaca;
  border-radius: 12px;
  color: #b91c1c;
  background: #fef2f2;
  font-size: 13px;
}

.service-layout {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 16px;
  flex: 1;
  min-height: 0;
}

.service-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: 1px solid #e8edf3;
  border-radius: 20px;
  background: #fff;
  overflow: hidden;
}

.service-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid #eef2f7;
}

.service-panel__head strong {
  color: #111827;
  font-size: 14px;
}

.service-panel__head span {
  color: #6b7280;
  font-size: 12px;
}

.conversation-list {
  flex: 1;
  overflow-y: auto;
}

.conversation-row {
  width: 100%;
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border: none;
  border-bottom: 1px solid #f3f6fa;
  background: #fff;
  text-align: left;
  cursor: pointer;
}

.conversation-row:hover {
  background: #f8fafc;
}

.conversation-row.active {
  background: #eef7f2;
}

.conversation-avatar {
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: 10px;
  color: #059669;
  background: #ecfdf5;
  font-size: 14px;
  font-weight: 600;
}

.conversation-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.conversation-main strong {
  color: #111827;
  font-size: 13.5px;
}

.kind-tag {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 8px;
  color: #0d7a4d;
  background: #e8f7ef;
  font-size: 11px;
  font-style: normal;
  font-weight: 500;
  vertical-align: 1px;
}

.conversation-main small {
  overflow: hidden;
  color: #9ca3af;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.conversation-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.conversation-side em {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  display: grid;
  place-items: center;
  border-radius: 9px;
  color: #fff;
  background: #ef4444;
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
}

.conversation-side small {
  color: #c0c7d0;
  font-size: 11px;
}

.message-list {
  flex: 1;
  overflow-y: auto;
  padding: 18px;
  background: #f8fafc;
}

.message-empty {
  margin: 40px 0 0;
  color: #9ca3af;
  font-size: 13px;
  text-align: center;
}

.message-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 14px;
}

.message-row.theirs {
  align-items: flex-start;
}

.message-row.mine {
  align-items: flex-end;
}

.message-bubble {
  max-width: 70%;
  padding: 10px 14px;
  border-radius: 12px;
  color: #1f2937;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  font-size: 13.5px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.message-row.mine .message-bubble {
  color: #fff;
  background: #10b981;
}

.message-bubble--image {
  width: min(360px, 68%);
  padding: 0;
  overflow: hidden;
  background: transparent;
  box-shadow: none;
}

.message-bubble--image a {
  display: block;
  cursor: zoom-in;
}

.message-bubble--image img {
  width: 100%;
  max-height: 360px;
  display: block;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.12);
  object-fit: contain;
}

.message-row small {
  color: #b6bdc7;
  font-size: 11.5px;
}

.message-composer {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid #eef2f7;
}

.message-composer textarea {
  min-height: 44px;
  max-height: 120px;
  flex: 1;
  padding: 11px 12px;
  border: 1px solid #e5eaf1;
  border-radius: 10px;
  outline: none;
  resize: vertical;
  font-family: inherit;
  font-size: 13.5px;
  line-height: 1.5;
}

.message-composer textarea:focus {
  border-color: #10b981;
}
</style>
